import React from "react";
import parse from "html-react-parser";
import { Field } from "react-final-form";
import RequiredAsterisk from "../UI/RequiredAsterisk";
import FieldError from "../FieldError";
import Condition from "../Conditionals/Condition";

import Tooltip from "../UI/FormTooltip";
import FieldResolver from "../FieldResolver";
import { validator } from "../validation";
import Label from "../UI/Label";

import FieldDisabled from "../FieldDisabled";

//import "../TableFormGenerator/TableFormGenerator.scss";

const warningStyles = {
  backgroundColor: "#e9e9e9",
  border: "1px solid #d2d3d6",
  padding: "5px 16px 5px 21px",
  width: "100%",
  marginTop: "1rem",
};

const Cell = ({
  cellSchema,
  formApi,
  idx,
  counties,
  addPageVisited,
  addPageErrored,
  removePageVisited,
  removePageErrored,
  currentErrors,
  returningToForm,
  returningToPage,
}) => {
  const {
    widget,
    label,
    machine_name,
    validate,
    disabled,
    disableWhenField,
    tooltip,
    hideAsterisk,
    cellWarning,
    maxLength,
  } = cellSchema;

  let isDisabled = false;
  if (disabled && disableWhenField !== undefined) {
    if (typeof disableWhenField === "string") {
      isDisabled = FieldDisabled(disabled, disableWhenField, formApi);
    } else {
      isDisabled = FieldDisabled(
        disabled,
        disableWhenField.when,
        formApi,
        undefined,
        disableWhenField.is
      );
    }
  } else {
    isDisabled = disabled;
  }

  let showRequired =
    (!!validate && validate[0] === "required") ||
    (!!validate?.[0].isRequired &&
      validate?.[0].extraData?.valueOf &&
      formApi.getState().values[validate?.[0].isRequired] ===
        validate?.[0].extraData.valueOf);

  if (hideAsterisk === true) {
    showRequired = false;
  }

  if (widget === "label") {
    return (
      <td align="left" style={{ verticalAlign: "top" }}>
        <Label
          style={{
            display: "flex",
            justifyContent: "flex-start",
            paddingLeft: "5px",
          }}
        >
          {label}
        </Label>
      </td>
    );
  }

  if (!!label) {
    return (
      <td
        style={{
          paddingTop: "2px",
          paddingBottom: "0.5em",
          verticalAlign: "top",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            paddingTop: "0.5em",
            paddingLeft: "0.5em",
          }}
        >
          <div
            style={{
              paddingTop: "0.5em",
              paddingRight: "0.5em",
              width: "40px",
            }}
          >
            <label htmlFor={`${machine_name}-${idx}`}>{label}</label>
            <RequiredAsterisk
              //Always show to prevent layout displacement, using visibility instead.
              show={true}
              style={{
                visibility: showRequired ? "visible" : "hidden",
              }}
            />
          </div>
          <div
            style={{
              minWidth: "190px",
              display: "flex",
              justifyContent: "center",
              paddingLeft: "0.2rem",
            }}
          >
            <Field
              key={`generic-table-${machine_name}-${idx}`}
              id={machine_name}
              name={machine_name}
              component={FieldResolver(widget)}
              schemaProps={cellSchema}
              formApi={formApi}
              disabled={isDisabled}
              validate={(values, allValues) =>
                validate
                  ? validator(validate, allValues, null, counties)(values)
                  : undefined
              }
              addPageVisited={addPageVisited}
              addPageErrored={addPageErrored}
              removePageVisited={removePageVisited}
              removePageErrored={removePageErrored}
              showRequired={showRequired}
              maxLength={maxLength}
            />
            {tooltip && <Tooltip content={tooltip} />}
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <FieldError
            field_name={machine_name}
            returningToPage={returningToPage}
            returningToForm={returningToForm}
            currentErrors={currentErrors}
          />
          {!!cellWarning && (
            <Condition
              when={cellWarning.isVisible.when}
              is={cellWarning.isVisible.is}
              operator={cellWarning.isVisible.operator}
              formApi={formApi}
            >
              <div style={warningStyles}>{parse(cellWarning.text)}</div>
            </Condition>
          )}
        </div>
      </td>
    );
  }
  return (
    <td align="center">
      <div
        style={{ padding: widget === "checkbox" ? "0.8em 0 0 0.2em" : "0em" }}
      >
        <Field
          key={`generic-table-${machine_name}-${idx}`}
          id={machine_name}
          name={machine_name}
          component={FieldResolver(widget)}
          schemaProps={cellSchema}
          disabled={isDisabled}
          formApi={formApi}
        />
      </div>
    </td>
  );
};

const RowFields = ({
  formApi,
  row,
  counties,
  addPageVisited,
  addPageErrored,
  removePageVisited,
  removePageErrored,
  currentErrors,
  returningToForm,
  returningToPage,
}) => (
  <tr>
    {row.map((cellSchema, idx) => (
      <Cell
        key={idx}
        cellSchema={cellSchema}
        idx={idx}
        formApi={formApi}
        counties={counties}
        addPageVisited={addPageVisited}
        addPageErrored={addPageErrored}
        removePageVisited={removePageVisited}
        removePageErrored={removePageErrored}
        currentErrors={currentErrors}
        returningToForm={returningToForm}
        returningToPage={returningToPage}
      />
    ))}
  </tr>
);

const GenericTable = ({
  schemaProps: { rows, headers, hideEmptyHeader, width, minWidth },
  formApi,
  counties,
  addPageVisited,
  addPageErrored,
  removePageVisited,
  removePageErrored,
  currentErrors,
  returningToForm,
  returningToPage,
}) => (
  <table
    style={{
      border: 1,
      display: "table",
      width: width || "100%",
      tableLayout: "fixed",
      minWidth: minWidth || "auto",
    }}
    className="input-table"
  >
    <thead>
      <tr>
        {!hideEmptyHeader && (
          <th
            style={{ display: "table-cell", width: "20px" }}
            className="table-headers"
          >
            {null}
          </th>
        )}
        {headers.map(({ label, tooltip }, idx) => (
          <th
            key={idx}
            style={{
              display: "table-cell",
              // width: "100px",
            }}
            className="table-headers"
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              {label}
              {tooltip && <Tooltip content={tooltip} />}
            </div>
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((row, idx) => (
        <RowFields
          key={idx}
          row={row}
          formApi={formApi}
          counties={counties}
          addPageVisited={addPageVisited}
          addPageErrored={addPageErrored}
          removePageVisited={removePageVisited}
          removePageErrored={removePageErrored}
          currentErrors={currentErrors}
          returningToForm={returningToForm}
          returningToPage={returningToPage}
        />
      ))}
    </tbody>
  </table>
);

export default GenericTable;
